import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, OutlinedInput, Skeleton, Stack, Typography } from '@mui/material';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import type { SearchableSelectListProps } from '../types';

const DEFAULT_LIST_MAX_HEIGHT = 380;
const DEFAULT_SKELETON_COUNT = 3;
const DEFAULT_SKELETON_HEIGHT = 76;

export const SearchableSelectList = ({
  searchPlaceholder,
  searchQuery,
  onSearchChange,
  isLoading,
  emptyMessage,
  noResultsMessage,
  children,
  maxHeight = DEFAULT_LIST_MAX_HEIGHT,
  testIds,
}: SearchableSelectListProps) => {
  const showList = !isLoading && !emptyMessage && !noResultsMessage;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <OutlinedInput
        size="small"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        }
        inputProps={testIds?.search ? { 'data-testid': testIds.search } : undefined}
      />

      {isLoading && (
        <Stack
          spacing={1.5}
          sx={{ maxHeight, overflowY: 'hidden', pr: 0.5 }}
          data-testid={testIds?.loading}
        >
          {Array.from({ length: DEFAULT_SKELETON_COUNT }).map((_, idx) => (
            <Skeleton
              key={idx}
              variant="rounded"
              height={DEFAULT_SKELETON_HEIGHT}
              animation="wave"
              sx={{ borderRadius: `${borderRadius.md}px` }}
            />
          ))}
        </Stack>
      )}

      {emptyMessage && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 4, textAlign: 'center' }}
          data-testid={testIds?.empty}
        >
          {emptyMessage}
        </Typography>
      )}

      {noResultsMessage && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 4, textAlign: 'center' }}
          data-testid={testIds?.noResults}
        >
          {noResultsMessage}
        </Typography>
      )}

      {showList && (
        <Stack spacing={0} sx={{ maxHeight, overflowY: 'auto', pr: 0.5 }}>
          {children}
        </Stack>
      )}
    </Box>
  );
};
